import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Baby, Plus, Trash2, Check, X, Pencil, Link, Unlink } from 'lucide-react';
import { getCrecheChildren, createCrecheChild, updateCrecheChild, deleteCrecheChild } from '../../api';
import { useStructure } from '../../contexts/StructureContext';

const STATUS_BADGES = {
  manuel: { label: 'Manuel', color: 'bg-blue-50 text-blue-700' },
  pre_inscription: { label: 'Pre-inscription', color: 'bg-orange-50 text-orange-700' },
  inscrit: { label: 'Inscrit', color: 'bg-green-50 text-green-700' },
  a_venir: { label: 'A venir', color: 'bg-purple-50 text-purple-700' },
  sorti: { label: 'Sorti', color: 'bg-gray-100 text-gray-500' },
};

const ChildManagement = () => {
  const { currentStructure, structures } = useStructure();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', birthDate: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', birthDate: '', status: 'manuel' });

  const fetchChildren = useCallback(async () => {
    if (!currentStructure) return;
    try {
      setLoading(true);
      const response = await getCrecheChildren({ structureId: currentStructure.id });
      setChildren(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des enfants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentStructure]);

  useEffect(() => { fetchChildren(); }, [fetchChildren]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !currentStructure) return;
    try {
      setLoading(true);
      setError(null);
      await createCrecheChild({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || null,
        birthDate: form.birthDate || null,
        structureId: currentStructure.id,
      });
      setForm({ firstName: '', lastName: '', birthDate: '' });
      setShowForm(false);
      fetchChildren();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Cet enfant existe deja dans cette structure');
      } else {
        setError("Erreur lors de l'ajout");
      }
      setLoading(false);
    }
  };

  const startEdit = (child) => {
    setEditId(child.id);
    setEditForm({
      firstName: child.firstName || '',
      lastName: child.lastName || '',
      birthDate: child.birthDate ? child.birthDate.slice(0, 10) : '',
      status: child.status || 'manuel',
    });
  };

  const handleUpdate = async () => {
    if (!editForm.firstName.trim()) return;
    try {
      setLoading(true);
      setError(null);
      await updateCrecheChild(editId, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim() || null,
        birthDate: editForm.birthDate || null,
        status: editForm.status,
      });
      setEditId(null);
      fetchChildren();
    } catch (err) {
      setError("Erreur lors de la modification");
      setLoading(false);
    }
  };

  const handleDelete = async (child) => {
    if (!window.confirm(`Supprimer "${child.firstName}${child.lastName ? ' ' + child.lastName : ''}" de la liste ?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteCrecheChild(child.id);
      fetchChildren();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  if (!currentStructure) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card><CardContent><p className="text-gray-500 py-8 text-center">Selectionnez une structure dans le selecteur en haut de page.</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Baby size={20} />
            Enfants — {currentStructure.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Liste des enfants. Vous pouvez modifier le prenom, nom, date de naissance et statut.
            {structures.length > 1 && (
              <span className="block mt-1 text-xs text-primary">La liste est propre a chaque structure — changez de structure via le selecteur en haut.</span>
            )}
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">{error}</div>
          )}

          {showForm && (
            <form onSubmit={handleAdd} className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[120px]">
                  <label className="block text-xs text-gray-500 mb-1">Prenom *</label>
                  <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="Prenom" required autoFocus />
                </div>
                <div className="flex-1 min-w-[120px]">
                  <label className="block text-xs text-gray-500 mb-1">Nom</label>
                  <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Nom (optionnel)" />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs text-gray-500 mb-1">Date de naissance</label>
                  <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    value={form.birthDate} onChange={(e) => setForm({ ...form, birthDate: e.target.value })} />
                </div>
                <div className="flex gap-1">
                  <button type="submit" className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                    disabled={loading || !form.firstName.trim()}>
                    <Check size={16} /> Ajouter
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setForm({ firstName: '', lastName: '', birthDate: '' }); }}
                    className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition"><X size={16} /></button>
                </div>
              </div>
            </form>
          )}

          {loading && children.length === 0 ? (
            <p className="text-gray-500 py-4 text-center text-sm">Chargement...</p>
          ) : children.length === 0 ? (
            <p className="text-gray-500 py-6 text-center text-sm">Aucun enfant configure pour cette structure.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-2 px-2">Prenom</th>
                    <th className="py-2 px-2">Nom</th>
                    <th className="py-2 px-2">Date de naissance</th>
                    <th className="py-2 px-2 text-center">Statut</th>
                    <th className="py-2 px-2 text-center">Meeko</th>
                    <th className="py-2 px-2 w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {children.map((child) => (
                    <tr key={child.id} className="border-b hover:bg-gray-50 transition">
                      {editId === child.id ? (
                        <>
                          <td className="py-2 px-2">
                            <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary text-sm"
                              value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} autoFocus />
                          </td>
                          <td className="py-2 px-2">
                            <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary text-sm"
                              value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} placeholder="(optionnel)" />
                          </td>
                          <td className="py-2 px-2">
                            <input type="date" className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary text-sm"
                              value={editForm.birthDate} onChange={(e) => setEditForm({ ...editForm, birthDate: e.target.value })} />
                          </td>
                          <td className="py-2 px-2 text-center">
                            <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                              className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary">
                              {Object.entries(STATUS_BADGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                          </td>
                          <td className="py-2 px-2 text-center">
                            {child.meekoId ? <Link size={14} className="mx-auto text-blue-500" /> : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <div className="flex justify-end gap-1">
                              <button onClick={handleUpdate} className="p-1 text-green-600 hover:bg-green-50 rounded transition"
                                disabled={loading || !editForm.firstName.trim()} title="Enregistrer"><Check size={16} /></button>
                              <button onClick={() => setEditId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded transition" title="Annuler"><X size={16} /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-2 px-2 font-medium text-gray-800">{child.firstName}</td>
                          <td className="py-2 px-2 text-gray-600">{child.lastName || '-'}</td>
                          <td className="py-2 px-2 text-gray-600">{fmtDate(child.birthDate)}</td>
                          <td className="py-2 px-2 text-center">
                            {(() => {
                              const s = STATUS_BADGES[child.status] || STATUS_BADGES.manuel;
                              return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
                            })()}
                          </td>
                          <td className="py-2 px-2 text-center">
                            {child.meekoId ? (
                              <span title={`Meeko ID: ${child.meekoId}`}><Link size={14} className="mx-auto text-blue-500" /></span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <div className="flex justify-end gap-1">
                              <button onClick={() => startEdit(child)} className="p-1 text-gray-400 hover:text-primary rounded transition" title="Modifier" disabled={loading}>
                                <Pencil size={15} />
                              </button>
                              <button onClick={() => handleDelete(child)} className="p-1 text-gray-400 hover:text-red-500 rounded transition" title="Supprimer" disabled={loading}>
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {!showForm && (
        <button onClick={() => setShowForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter un enfant">
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default ChildManagement;
